import React, { useEffect, useMemo, useState } from 'react';
import { apiClient } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Flame, UserCheck, Ban, Inbox, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiErrorMessage } from '@/lib/apiError';

export const STAGE_LABEL = { new_lead: 'New Lead', call_back: 'Call Back', warm_lead: 'Warm', hot_lead: 'Hot', enrolled: 'Enrolled', rejected: 'Rejected', not_interested: 'Not Interested' };
export const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
export const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
export const daysAgo = (iso) => (iso ? Math.max(0, Math.floor((Date.now() - new Date(iso)) / 86400000)) : null);

export function ColumnFilter({ col, value, onChange, options }) {
  if (col.filter === 'none') return <th className="px-2 py-1.5" />;
  if (col.filter === 'select') {
    return (
      <th className="px-2 py-1.5">
        <select data-testid={`returning-filter-${col.key}`} className="h-7 w-full min-w-[90px] rounded border bg-background px-1 text-xs font-normal normal-case tracking-normal"
          value={value || ''} onChange={e => onChange(e.target.value)}>
          <option value="">All</option>
          {options.map(o => <option key={o} value={o}>{col.render ? col.render(o) : o}</option>)}
        </select>
      </th>
    );
  }
  return (
    <th className="px-2 py-1.5">
      <Input data-testid={`returning-filter-${col.key}`} className="h-7 text-xs font-normal normal-case tracking-normal min-w-[90px]" placeholder="Filter…" value={value || ''} onChange={e => onChange(e.target.value)} />
    </th>
  );
}

export function TimelineDialog({ rec, onClose }) {
  if (!rec) return null;
  const log = [...(rec.seen_log || [])].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  const missing = Math.max(0, (rec.times_seen || 1) - log.length);
  const events = [
    rec.lead_created_at && { date: rec.lead_created_at, kind: 'original', title: 'First inquiry', detail: `Lead created in ERP${rec.lead_source ? ` via ${rec.lead_source.replace(/_/g, ' ')}` : ''}` },
    rec.lead_assigned_at && { date: rec.lead_assigned_at, kind: 'assigned', title: `Assigned to ${rec.in_rejected_pool ? (rec.rejected_by || 'agent') : (rec.existing_owner_name || 'agent')}`, detail: rec.existing_team || '' },
    rec.in_rejected_pool && rec.rejected_at && { date: rec.rejected_at, kind: 'rejected', title: `Rejected by ${rec.rejected_by || '—'}`, detail: (rec.rejection_reason || 'no reason').replace(/_/g, ' ') },
    ...log.map((s, i) => ({ date: s.date, kind: 'return', title: `Came back again (#${i + 1})`, detail: [s.source, s.sheet_agent && `sheet agent: ${s.sheet_agent}`, s.name && s.name !== rec.full_name && `as "${s.name}"`].filter(Boolean).join(' · ') })),
    rec.status === 'assigned' && rec.handled_at && { date: rec.handled_at, kind: 'handled', title: `Re-assigned to ${rec.assigned_to_name}`, detail: `by ${rec.assigned_by_name || ''}` },
    rec.status === 'dismissed' && rec.handled_at && { date: rec.handled_at, kind: 'handled', title: 'Dismissed', detail: `by ${rec.dismissed_by_name || ''}` },
  ].filter(Boolean).sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  const span = log.length > 1 ? daysAgo(log[0].date) - daysAgo(log[log.length - 1].date) : null;
  const color = { original: 'bg-slate-400', assigned: 'bg-blue-500', rejected: 'bg-red-500', return: 'bg-orange-500', handled: 'bg-emerald-500' };
  const Icon = { original: Inbox, assigned: UserCheck, rejected: Ban, return: Flame, handled: UserCheck };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="returning-timeline-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Flame className="w-5 h-5 text-orange-500" />{rec.full_name} <span className="font-mono text-sm text-muted-foreground">{(rec.phones?.length ? rec.phones : [rec.phone]).join(' / ')}</span></DialogTitle>
          <DialogDescription>
            Came back <b>{rec.times_seen}×</b>{span ? <> within <b>{span} days</b></> : null}. Last on {fmt(rec.last_seen_at)} ({daysAgo(rec.last_seen_at)} days ago).
          </DialogDescription>
        </DialogHeader>
        <ol className="relative border-l ml-3 space-y-4 max-h-[55vh] overflow-y-auto pr-2" data-testid="returning-timeline-list">
          {events.map((e, i) => { const I = Icon[e.kind]; return (
            <li key={i} className="ml-5">
              <span className={`absolute -left-[9px] mt-1 flex h-4 w-4 items-center justify-center rounded-full ${color[e.kind]} ring-4 ring-background`}><I className="w-2.5 h-2.5 text-white" /></span>
              <div className="text-xs text-muted-foreground">{fmtTime(e.date)}</div>
              <div className="text-sm font-medium">{e.title}</div>
              {e.detail && <div className="text-xs text-muted-foreground">{e.detail}</div>}
            </li>); })}
        </ol>
        {missing > 0 && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">+{missing} more return{missing > 1 ? 's' : ''} between {fmt(log[0]?.date)} and {fmt(rec.last_seen_at)} — exact dates were not captured in the historical backfill.</p>}
      </DialogContent>
    </Dialog>
  );
}

export function AssignDialog({ rec, agents, teams, onClose, onDone }) {
  const [agentId, setAgentId] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [moveIds, setMoveIds] = useState([]);
  const linked = useMemo(() => (rec?.linked_leads || []), [rec]);
  useEffect(() => {
    if (!rec) return;
    const hint = (rec.sheet_agent || '').split(',')[0].trim().toLowerCase();
    const m = hint && agents.find(a => (a.full_name || '').trim().toLowerCase().startsWith(hint.split(' ')[0]));
    setAgentId(m ? m.id : ''); setNote('');
    setMoveIds(linked.filter(l => l.stage !== 'enrolled').map(l => l.lead_id));
  }, [rec, agents, linked]);
  const grouped = useMemo(() => {
    const byTeam = {};
    agents.forEach(a => { const t = teams.find(x => x.id === a.team_id)?.name || 'No team'; (byTeam[t] = byTeam[t] || []).push(a); });
    return Object.entries(byTeam).sort(([a], [b]) => a.localeCompare(b));
  }, [agents, teams]);
  const submit = async () => {
    if (!agentId) return toast.error('Pick an agent');
    setSaving(true);
    try {
      const r = await apiClient.post(`/sales/reinquiries/${rec.id}/assign`, { agent_id: agentId, note: note || undefined, move_linked_lead_ids: moveIds });
      const n = (r.data.moved_linked || []).length;
      toast.success(`Assigned to ${r.data.assigned_to_name} as a New Lead${n ? ` · ${n} linked number${n > 1 ? 's' : ''} moved along` : ''}`); onDone();
    } catch (e) { toast.error(apiErrorMessage(e, 'Failed to assign'), { duration: 9000 }); } finally { setSaving(false); }
  };
  const phones = rec?.phones?.length ? rec.phones : [rec?.phone];
  return (
    <Dialog open={!!rec} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="returning-assign-dialog">
        <DialogHeader>
          <DialogTitle>Assign returning lead</DialogTitle>
          <DialogDescription>{rec?.full_name} — will be re-opened as a <b>New Lead</b> with a "Returning Lead" tag.</DialogDescription>
        </DialogHeader>
        <div className="rounded-md border bg-muted/40 p-2.5 text-xs space-y-1" data-testid="returning-assign-phones">
          <div className="font-semibold text-muted-foreground uppercase tracking-wide">Numbers going with this lead</div>
          {phones.map(p => <div key={p} className="font-mono">{p}</div>)}
        </div>
        {linked.length > 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs space-y-1.5" data-testid="returning-assign-linked">
            <div className="font-semibold text-amber-800 flex items-center gap-1"><Link2 className="w-3.5 h-3.5" />One of these numbers is also on another lead</div>
            {linked.map(l => (
              <label key={l.lead_id} className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" className="mt-0.5" data-testid={`returning-move-linked-${l.lead_id}`} disabled={l.stage === 'enrolled'}
                  checked={moveIds.includes(l.lead_id)} onChange={e => setMoveIds(ids => e.target.checked ? [...ids, l.lead_id] : ids.filter(x => x !== l.lead_id))} />
                <span><span className="font-mono">{l.phone}</span> — "{l.full_name}" {l.stage === 'enrolled' ? <b>is an enrolled customer (not moved)</b> : <>currently <b>{l.in_rejected_pool ? `rejected (${l.owner_name || 'pool'})` : `${STAGE_LABEL[l.stage] || l.stage} with ${l.owner_name || '—'}`}</b> → also move to this agent</>}</span>
              </label>))}
          </div>
        )}
        <Select value={agentId} onValueChange={setAgentId}>
          <SelectTrigger data-testid="returning-assign-agent"><SelectValue placeholder="Choose agent" /></SelectTrigger>
          <SelectContent className="max-h-80">
            {grouped.map(([team, list]) => (
              <SelectGroup key={team}><SelectLabel>{team}</SelectLabel>
                {list.map(a => <SelectItem key={a.id} value={a.id}>{(a.full_name || '').trim()}{a.role === 'team_leader' ? ' (TL)' : ''}</SelectItem>)}
              </SelectGroup>))}
          </SelectContent>
        </Select>
        <Textarea data-testid="returning-assign-note" placeholder="Note for the agent (optional)" value={note} onChange={e => setNote(e.target.value)} rows={2} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="returning-assign-submit" onClick={submit} disabled={saving}>{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Assign</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TimesBadge({ rec, onClick }) {
  return (
    <button type="button" data-testid={`returning-times-${rec.id}`} onClick={onClick} title="See every time this customer came in" className="focus:outline-none">
      {rec.times_seen > 1
        ? <Badge variant="destructive" className="gap-1 cursor-pointer hover:brightness-110"><Flame className="w-3 h-3" />{rec.times_seen}×</Badge>
        : <Badge variant="outline" className="cursor-pointer hover:bg-muted">1×</Badge>}
    </button>
  );
}
